import { Reports_GetReportTemplateQuery, ReportScheduleRecurrence } from '@/__generated__/graphql';
import { useAuth, useModalStatusMessage } from '@/hooks';
import { ApolloError, skipToken, useMutation, useSuspenseQuery } from '@apollo/client';
import CREATE_REPORT_TEMPLATE from '@features/reports2/graphql/mutation/createReportTemplate';
import EXECUTE_REPORT from '@features/reports2/graphql/mutation/executeReport';
import UPDATE_REPORT_TEMPLATE from '@features/reports2/graphql/mutation/updateReportTemplate';
import { GET_REPORT_TEMPLATE } from '@features/reports2/graphql/queries/getReportTemplate';
import { parseTimeRange } from '@features/reports2/utils/parseTimeRange';
import { getBrowserTimezone } from '@features/reports2/utils/timezone';
import { zodResolver } from '@hookform/resolvers/zod';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { DefaultValues, useForm } from 'react-hook-form';
import { CreateReportFieldValues, CreateReportSchema } from './validation';
import { GET_TEMPLATES } from '@features/reports2/graphql/queries/getReportTemplates';
import { getHoursAndMinutes } from '@features/reports2/utils/schedule';

const now = dayjs();

const getDefaultValues = (
  userEmail: string | null | undefined,
  data?: Reports_GetReportTemplateQuery,
) => {
  const values: DefaultValues<CreateReportFieldValues> = {
    firstStep: {
      devices: [],
      metrics: [],
    },
    secondStep: {
      period: {
        start: '-7d',
        stop: null,
      },
      meanWindow: '1h',
      timezone: getBrowserTimezone(),
    },
    thirdStep: {
      createExcel: false,
      createPdf: false,
      emails: userEmail ? [{ email: userEmail }] : [],
    },
    fourthStep: {
      enabled: false,
      schedule: {
        recurrence: ReportScheduleRecurrence.Daily,
        time: now,
      },
    },
    fifthStep: {
      saveTemplate: false,
    },
  };

  if (data) {
    const template = data.reportTemplate;
    values.firstStep = {
      devices: template.devices.map((device) => device.id),
      metrics: template.metrics.map((metric) => ({ name: metric.name, unit: metric.unit })),
    };
    values.secondStep = {
      meanWindow: template.meanWindow,
      period: { start: template.relativeTime, stop: null },
      timezone: template.timezone,
    };
    values.thirdStep = {
      createExcel: template.createExcel,
      createPdf: template.createPdf,
      emails: template.emails.map((email) => ({ email })),
    };
    const schedule = template.schedules.at(0);
    if (schedule) {
      const { hours, minutes } = schedule;
      const time = now.set('hours', hours).set('minutes', minutes);
      values.fourthStep = {
        enabled: schedule != null,
        schedule: {
          recurrence: schedule.recurrence,
          dayOfWeek: schedule.dayOfWeek ?? null,
          daysOfWeek: schedule.daysOfWeek ?? null,
          time,
        },
      };
    }
    values.fifthStep = {
      saveTemplate: true,
      templateName: template.name,
      templateDescription: template.description ?? undefined,
    };
  }

  return values;
};

export const useCreateForm = ({
  templateId,
  onClose,
}: {
  templateId?: number;
  onClose: (skipCloseConfirmation?: boolean) => void;
}) => {
  const { _ } = useLingui();
  const { user } = useAuth();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const { data } = useSuspenseQuery(
    GET_REPORT_TEMPLATE,
    templateId
      ? {
          variables: {
            id: templateId,
          },
        }
      : skipToken,
  );

  const defaultValues = useMemo(() => {
    return getDefaultValues(user?.email, data);
  }, [data, user]);

  const [createTemplate] = useMutation(CREATE_REPORT_TEMPLATE);
  const [executeReport] = useMutation(EXECUTE_REPORT);
  const [updateReportTemplate] = useMutation(UPDATE_REPORT_TEMPLATE);

  const methods = useForm<CreateReportFieldValues>({
    resolver: zodResolver(CreateReportSchema),
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const onSubmit = methods.handleSubmit(async (data) => {
    const period = parseTimeRange(data.secondStep.period.start, data.secondStep.period.stop);

    const parameters = {
      onCompleted() {
        onClose(true);
        openModalSuccessMessage(
          _(msg`Se ha encolado la generación del reporte, lo recibirás en unos momentos`),
        );
      },
      onError(error: ApolloError) {
        openModalErrorMessage(
          _(msg`Ha ocurrido un error al ejecutar el reporte`) + ' ' + error.message,
        );
      },
    };

    // Update template
    if (templateId) {
      const relativeTime = period.relativeTime;
      const { templateName, templateDescription } = data.fifthStep;
      if (!relativeTime || !templateName) return;

      const schedule =
        data.fourthStep.enabled && data.fourthStep.schedule ? data.fourthStep.schedule : null;

      await updateReportTemplate({
        variables: {
          input: {
            id: templateId,
            createPdf: data.thirdStep.createPdf,
            createExcel: data.thirdStep.createExcel,
            devices: data.firstStep.devices,
            emails: data.thirdStep.emails.map(({ email }) => email),
            metrics: data.firstStep.metrics,
            timezone: data.secondStep.timezone,
            meanWindow: data.secondStep.meanWindow,
            name: templateName,
            description: templateDescription || null,
            relativeTime,
            schedule: schedule
              ? {
                  recurrence: schedule.recurrence,
                  timezone: data.secondStep.timezone,
                  daysOfWeek: schedule.daysOfWeek ?? null,
                  dayOfWeek: schedule.dayOfWeek ?? null,
                  ...getHoursAndMinutes(schedule.time),
                }
              : null,
          },
        },
        onCompleted() {
          onClose(true);
          openModalSuccessMessage(_(msg`Se ha guardado el reporte correctamente`));
        },
        onError(error: ApolloError) {
          openModalErrorMessage(
            _(msg`Ha ocurrido un error al guardar el reporte`) + ' ' + error.message,
          );
        },
        refetchQueries: [GET_TEMPLATES],
      });
    }

    // Only trigger without save
    else if (!data.fifthStep.saveTemplate) {
      await executeReport({
        variables: {
          input: {
            createPdf: data.thirdStep.createPdf,
            createExcel: data.thirdStep.createExcel,
            devices: data.firstStep.devices,
            emails: data.thirdStep.emails.map(({ email }) => email),
            metrics: data.firstStep.metrics,
            timezone: data.secondStep.timezone,
            meanWindow: data.secondStep.meanWindow,
            ...period,
          },
        },
        ...parameters,
      });
    } else {
      const relativeTime = period.relativeTime;
      const { templateName, templateDescription } = data.fifthStep;
      if (!relativeTime || !templateName) return;

      const schedule =
        data.fourthStep.enabled && data.fourthStep.schedule ? data.fourthStep.schedule : null;

      await createTemplate({
        variables: {
          input: {
            execute: true,
            createPdf: data.thirdStep.createPdf,
            createExcel: data.thirdStep.createExcel,
            devices: data.firstStep.devices,
            emails: data.thirdStep.emails.map(({ email }) => email),
            metrics: data.firstStep.metrics,
            timezone: data.secondStep.timezone,
            meanWindow: data.secondStep.meanWindow,
            name: templateName,
            description: templateDescription || null,
            relativeTime,
            schedule: schedule
              ? {
                  recurrence: schedule.recurrence,
                  timezone: data.secondStep.timezone,
                  daysOfWeek: schedule.daysOfWeek ?? null,
                  dayOfWeek: schedule.dayOfWeek ?? null,
                  ...getHoursAndMinutes(schedule.time),
                }
              : null,
          },
        },
        refetchQueries: [GET_TEMPLATES],
        ...parameters,
      });
    }
  });

  return { methods, onSubmit };
};
